import React,{useEffect, useState} from "react";
import {getFields} from "../../../../../dialog/formfields";
import {IconButton, Menu, MenuItem} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MDBox from "../../../../../../components/MDBox";
import TabPageHeader from "../../header/tabpageheader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GenericForm from "../../../../../dialog/genericform";
import DataTableConfig from "../../../../../Tables/DataTableConfig";
import PropTypes from "prop-types";
import tableData from "../../../../../tabledata";
import useConfigApi from "../../../../../utils/configapi";
import Endpoints from "../../../../../utils/endpoints";
import toast from "react-hot-toast";

const CurrencyConfig = ({ selectedRowData }) => {
    const { currencyConfigColumns } = tableData();
    const { currencyConfigFields } = getFields();
    const [currencyConfig, setCurrencyConfig] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [dialogData, setDialogData] = React.useState(null);
    const [dialogMode, setDialogMode] = React.useState("edit");
    const [dialogTitle, setDialogTitle] = React.useState("Create Currency Config");
    const api = useConfigApi();
    const [currencyConfigFieldsModified, setCurrencyConfigFieldsModified] = useState([]);


    const handleClick = () => {
        setDialogMode("create");
        setDialogData({});
        setModalOpen(true);
        setDialogTitle("Create Currency Config");

    };

    const closeModal = () => {
        setModalOpen(false);
        setDialogData(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setActiveRowIndex(null);
    };

    const handleMenuOpen = (event, index) => {
        setAnchorEl(event.currentTarget);
        setActiveRowIndex(index);
    };

    const handleActionClick = (action: string, item) => {
        const row = currencyConfig[activeRowIndex];
        if(action === 'View'){
            setModalOpen(true);
            setDialogMode("view");
            setDialogData({
                ...row,
                hostReference: row.hostReference.map(s => s.slNo).join(", ")
            });
            setDialogTitle("View Currency Config");
        } else if (action === 'Edit') {
            setModalOpen(true);
            setDialogMode("edit");
            setDialogTitle("Edit Currency Config");
            setDialogData({
                ...row,
                hostReference: row.hostReference.map(s => s.slNo).join(", ")
            });
        } else if (action === 'Delete') {
            setModalOpen(true);
            setDialogMode("delete");
            setDialogTitle("Delete Currency Config");
            setDialogData(row);
        }
        handleMenuClose();
    };

    const handleDialogSubmit = (formData) => {
        console.log("Submitted data:", formData, dialogMode);
        formData.controlConfigSerialNumber  = selectedRowData.slNo;
        // formData.hostConfigSerialNumber = formData.hostReference;
        formData.hostConfigSerialNumber = formData.hostReference.split(",")
        .map(s => s.trim())
        .filter(s => s.length > 0)
        .map(Number);
        // Call your API here
        if (dialogMode === "create") {
            // Handle create logic here
        api
          .post(Endpoints.currencyConfig.createOrUpdate, formData)
                .then((response) => {
                    toast.success("Currency config successfully created.");
                    fetchCurrencyConfigs().catch(console.error);
                })
                .catch((error) => {
                    console.log(error.response.data?.responseMessage)
                    toast.error(error.response.data?.responseMessage || "Internal error occurred while creating.");
                });
        } else if (dialogMode === "edit") {
            api.put(Endpoints.currencyConfig.createOrUpdate, formData)
                .then((response) => {
                  toast.success("Currency config successfully updated.");
                  const updatedPayload = data?.data; // if backend returns the updated row

                  setCurrencyConfig((prevRows) =>
                    prevRows.map((row, index) => {
                      if (index !== activeRowIndex) return row;
                      // Start with existing row to preserve cells like `action`
                      let nextRow = { ...row, ...(updatedPayload ?? formData) };
                      // If API returned hostReference objects, refresh the labels for the table
                      if (updatedPayload?.hostReference) {
                        nextRow.hostReferenceLabels = updatedPayload.hostReference.map(
                          (s) => s.label
                        );
                      }
                      return nextRow;
                    })
                  );
                  // keep order; no fetchCurrencyConfigs()
                })
                .catch((error) => {
                    console.log(error.response.responseMessage)
                    toast.error(error.response.data?.responseMessage || "Internal error occurred while updating.");
                });
        }
    };

    const handleDialogDelete = (formData) => {
        if (!formData.slNo) {
            toast.error("Missing slNo for deletion.");
            return;
        }
        api.delete(Endpoints.currencyConfig.deleteBySlNo(formData.slNo))
            .then(() => {
                toast.success(`Currency config ${formData.slNo} successfully deleted.`);
                fetchCurrencyConfigs().catch(console.error);
            })
            .catch(() => {
                toast.error("Error deleting header config.");
            });
    };

    useEffect(() => {
        fetchCurrencyConfigs().catch(console.error);
    },[]);

    const fetchCurrencyConfigs = async () => {
        try {
            // 1. Fetch Control Config
            const currencyConfigResponse = await api.get(Endpoints.currencyConfig.getByControlSlNo(selectedRowData.slNo));
            const currencyConfig = currencyConfigResponse?.data?.data || [];

            // 2. Fetch Groups
            const hostResponse = await api.get(Endpoints.hostConfig.getByControlSlNo(selectedRowData.slNo));
            const hosts = hostResponse?.data?.data || [];

            // 3. Format Table Data
            const modifiedData = currencyConfig.map((item, index) => {
                const action = (
                    <>
                        <IconButton onClick={(e) => handleMenuOpen(e, index)}>
                            <MoreHorizIcon />
                        </IconButton>
                    </>
                );
                const hostReferenceLabels = item.hostReference.map(s => s.label);
                return { ...item, action, hostReferenceLabels };
            });
            setCurrencyConfig(modifiedData);

            // 4. Prepare Options for Select Field
            const hostOptions = hosts.map((host) => ({
                label: host.label,
                value: host.slNo
            }));

            setCurrencyConfigFieldsModified(currencyConfigFields.map(field =>
                field.name === "hostReference"
                    ? { ...field, options: hostOptions}
                    : field
            ));

        } catch (error) {
            toast.error("Error while fetching currency config or host data.");
            console.error(error);
        }
    };


    return <>
        <MDBox
            sx={{
                px: 3,
                py: 2,
                bgcolor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <TabPageHeader
                title="Currency Config"
                showButton={true}
                buttonLabel = "Add"
                onButtonClick={handleClick}

            />
        </MDBox>
        <MDBox sx={{
            px: 3,
            py: 2,
            bgcolor: "#fff",
            borderBottom: "1px solid #d3e3fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            overflowX: "auto",
            width: "100%",
        }}>


            <DataTableConfig
                table={{ columns : currencyConfigColumns, rows : currencyConfig }}
                showTotalEntries={true}
                isSorted={true}
                noEndBorder
                entriesPerPage={true}
                canSearch={true}
            />
        </MDBox>
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
        >
            <MenuItem onClick={() => handleActionClick("View")}>
                <IconButton size="small">
                    <i className="material-icons">visibility</i>
                </IconButton>
                View
            </MenuItem>
            <MenuItem onClick={() => handleActionClick("Edit")}>
                <IconButton size="small">
                    <EditIcon fontSize="small" />
                </IconButton>
                Edit
            </MenuItem>
            <MenuItem onClick={() => handleActionClick("Delete")}>
                <IconButton size="small" color="error">
                    <DeleteIcon fontSize="small" />
                </IconButton>
                Delete
            </MenuItem>
            {/*<MenuItem onClick={() => handleActionClick("Sync")}>*/}
            {/*    <IconButton size="small" color="error">*/}
            {/*        <RefreshIcon fontSize="small" />*/}
            {/*    </IconButton>*/}
            {/*    Sync*/}
            {/*</MenuItem>*/}
        </Menu>
        <GenericForm
            open={isModalOpen}
            handleClose={closeModal}
            title={dialogTitle}
            fields={currencyConfigFieldsModified}
            onSubmit={handleDialogSubmit}
            onDelete={handleDialogDelete}
            initialData={dialogData}
            mode={dialogMode}
            useDialog={true}
        />
    </>
};
CurrencyConfig.propTypes = {
    selectedRowData: PropTypes.shape({
        controlName: PropTypes.string,
        controlType: PropTypes.string,
        slNo: PropTypes.number,
    }),
};
export default CurrencyConfig;
